import { Edit, Notebook } from '@carbon/icons-react';
import { updateUpstreamEventInternalStrategy } from 'client';
import React from 'react';
import { useRevalidator } from 'react-router';

import { Button } from 'DesignSystem/ui/button';
import { RichEditor } from 'DesignSystem/ui/rich-editor/rich-editor';
import { RichHtml } from 'DesignSystem/ui/rich-editor/rich-html';
import { isEmptyRichHtml, sanitizeRichHtml } from 'DesignSystem/ui/rich-editor/sanitize-rich-html';
import { toast } from 'DesignSystem/ui/use-toast';
import { useDisruptionBootstrapQuery } from 'pages/disruption/api';

import { useEventDetails } from '../event-detail.route';
import { EventOverviewCard } from './event-overview-card';

function formatStrategyUpdatedAt(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function lastUpdatedLabel(updatedBy?: string | null, updatedAt?: Date | string | null): string | null {
  const dateLabel = updatedAt ? formatStrategyUpdatedAt(updatedAt) : '';
  if (updatedBy && dateLabel) return `Last Updated by ${updatedBy} • ${dateLabel}`;
  if (updatedBy) return `Last Updated by ${updatedBy}`;
  if (dateLabel) return `Last Updated • ${dateLabel}`;
  return null;
}

export default function InternalStrategy({
  onUnsavedChangesChange,
}: {
  onUnsavedChangesChange?: (dirty: boolean) => void;
} = {}) {
  const data = useEventDetails();
  const event = data?.event;
  const { data: canEdit } = useDisruptionBootstrapQuery((bootstrap) => bootstrap.allowed_to_edit_events);
  const revalidator = useRevalidator();

  const savedHtml = event?.internal_strategy ?? '';
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(savedHtml);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!isEditing) {
      setDraft(savedHtml);
    }
  }, [isEditing, savedHtml]);

  if (!event) return null;

  const eventId = event.event_id;
  const footerLabel = lastUpdatedLabel(event.internal_strategy_updated_by, event.internal_strategy_updated_at);

  function startEditing() {
    setDraft(savedHtml);
    setIsEditing(true);
  }

  function handleDraftChange(html: string) {
    setDraft(html);
    onUnsavedChangesChange?.(true);
  }

  function cancelEditing() {
    setDraft(savedHtml);
    setIsEditing(false);
    onUnsavedChangesChange?.(false);
  }

  async function save() {
    if (isSaving) return;

    const sanitized = sanitizeRichHtml(draft);
    const nextValue = isEmptyRichHtml(sanitized) ? '' : sanitized;

    setIsSaving(true);
    try {
      const response = await updateUpstreamEventInternalStrategy({
        path: { event_uuid: eventId },
        body: { internal_strategy: nextValue },
      });
      if (response.error) {
        throw response.error;
      }
      setIsEditing(false);
      onUnsavedChangesChange?.(false);
      void revalidator.revalidate();
    } catch {
      toast({
        title: 'Failed to update Internal Strategy',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <EventOverviewCard
      icon={<Notebook size={16} />}
      title="Internal Strategy"
      actions={
        canEdit ? (
          isEditing ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={cancelEditing}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={() => void save()} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          ) : (
            <Button type="button" variant="outline" size="sm" className="bg-white" onClick={startEditing}>
              Edit
            </Button>
          )
        ) : null
      }
      footer={
        !isEditing && footerLabel ? (
          <div className="flex items-center gap-1.5 border-t border-zinc-200 px-4 py-2.5 text-xs text-zinc-500">
            <Edit size={14} />
            {footerLabel}
          </div>
        ) : null
      }
    >
      {isEditing ? (
        <RichEditor content={savedHtml} onChange={handleDraftChange} placeholder="Add an internal strategy..." />
      ) : (
        <RichHtml html={savedHtml} emptyLabel="No internal strategy yet" />
      )}
    </EventOverviewCard>
  );
}
